import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useFormStore } from "../store/formStore";
import { useUiStore } from "../store/uiStore";
import { asError, eventBus } from "@optimyl/events";
import { getApi } from "../api";
import { clearTabSfEnv, getTabSfEnv } from "../sfEnv";

/**
 * Floating dev-tools bubble for local testing.
 *
 * Renders a small fixed-position button in the bottom-right that expands
 * into a panel of debug actions. Gated on `config.SHOW_DEV_TOOLS` so it
 * never ships to production. Lives on FormPage so it's available across
 * all steps — add new debug actions here as needs arise rather than
 * scattering them through step components.
 */
export function DevToolsPanel() {
  const [open, setOpen] = useState(false);
  const formVersion = useFormStore((s) => s.formVersion);
  const [searchParams] = useSearchParams();
  const addToast = useUiStore((s) => s.addToast);
  const showValidationToast = useUiStore((s) => s.showValidationToast);
  const setShowValidationToast = useUiStore((s) => s.setShowValidationToast);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on outside click or Escape
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleTestError = () => {
    try {
      const obj: Record<string, unknown> = {};
      const nested = obj as { deep: { value: string } };
      console.log(nested.deep.value);
    } catch (err) {
      eventBus.publish("error", {
        error: asError(err),
        context: "DevToolsPanel.handleTestError",
      });
    }
  };

  const handleCreateNextVersion = async () => {
    const proposalId = searchParams.get("oppId");
    if (!proposalId) {
      addToast("Missing ?oppId= URL param; cannot create a new version.", "error");
      return;
    }
    try {
      const response = await getApi().createIntakeForm(proposalId);
      addToast(`Created V${response.version}. Reloading...`, "success", 2000);
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      eventBus.publish("error", {
        error: asError(err),
        context: "DevToolsPanel.handleCreateNextVersion",
      });
    }
  };

  return (
    <div ref={panelRef} className="fixed bottom-20 right-4 z-40">
      {open && (
        <div className="mb-2 w-80 rounded-lg border-2 border-dashed border-brand-navy bg-white p-4 shadow-lg">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-brand-navy">
            Dev Tools
          </p>

          <div className="mb-3 space-y-1 text-xs text-gray-600">
            <div>
              Version: <strong>V{formVersion ?? "?"}</strong>
            </div>
            {getTabSfEnv() && (
              <div>
                Environment: <strong>{getTabSfEnv()?.toUpperCase()}</strong>
              </div>
            )}
          </div>

          <label className="mb-3 flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={showValidationToast}
              onChange={(e) => setShowValidationToast(e.target.checked)}
              className="h-4 w-4 cursor-pointer"
            />
            <span>
              Show <em>"fields require attention"</em> toast
            </span>
          </label>

          <div className="flex flex-col gap-2">
            <button
              onClick={() => {
                clearTabSfEnv();
                window.location.href = "/env";
              }}
              className="rounded-lg bg-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-300 cursor-pointer"
            >
              Change Environment
            </button>
            <button
              onClick={handleCreateNextVersion}
              className="rounded-lg bg-brand-navy px-4 py-2 text-sm text-white hover:bg-brand-navy-light cursor-pointer"
            >
              Create Next Version
            </button>
            <button
              onClick={handleTestError}
              className="rounded-lg bg-danger px-4 py-2 text-sm text-white hover:opacity-90"
            >
              Test Error (App Insights)
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-dashed border-brand-navy bg-white text-xs font-bold text-brand-navy shadow-md hover:bg-gray-50"
        aria-label={open ? "Close dev tools" : "Open dev tools"}
        title="Dev Tools"
      >
        DEV
      </button>
    </div>
  );
}
